"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Mail, CircleCheck as CheckCircle2, ArrowRight, Loader as Loader2, ShieldCheck, CalendarCheck, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const projectTypeOptions = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
const contactMethodOptions = ["Phone", "Text", "Email"];
const timingOptions = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
const budgetOptions = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
const contactCards = [{
  icon: Phone,
  title: "Call our team",
  detail: "(470) 847-9076",
  note: "Direct line for consultation inquiries",
  action: "tel:+14708479076"
}, {
  icon: Mail,
  title: "Send an email",
  detail: "serviceleadalerts@gmail.com",
  note: "Direct inbox for project inquiries",
  action: "mailto:serviceleadalerts@gmail.com"
}];
const perks = [{
  title: "Accurate scoping",
  description: "Detailed on-site evaluations with zero guesswork."
}, {
  title: "Dedicated project lead",
  description: "A single point of contact managing your project details."
}];
function EstimateForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget);
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", typeof window !== "undefined" ? window.location.pathname : "");
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params.toString()
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setSubmitError("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setSubmitError("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      address: "",
      project_type: "",
      details: "",
      contact_method: "",
      timing: "",
      budget: "",
      consent: false
    });
    setIsSubmitted(false);
    setSubmitError(null);
  };
  if (isSubmitted) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card/90 border border-border rounded-xl shadow-md space-y-5">
        <div className="size-14 rounded-lg bg-accent flex items-center justify-center text-primary">
          <CheckCircle2 className="size-7" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-semibold text-foreground">
            Request Received
          </h3>
          <p className="text-muted-foreground max-w-md text-sm">
            Thank you for reaching out. We have received your project details and will be in touch to discuss your consultation.
          </p>
        </div>
        <Button type="button" onClick={handleReset} variant="outline" className="mt-2">
          Submit another inquiry
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="form-name" className="text-foreground text-sm font-medium">
            Full Name <span className="text-destructive">*</span>
          </label>
          <Input id="form-name" name="name" type="text" required placeholder="Jane Doe" value={formData.name} onChange={e => setFormData(prev => ({
          ...prev,
          name: e.target.value
        }))} className="bg-card text-foreground border-border focus-visible:ring-ring" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="form-phone" className="text-foreground text-sm font-medium">
            Phone Number <span className="text-destructive">*</span>
          </label>
          <Input id="form-phone" name="phone" type="tel" required placeholder="(470) 847-9076" value={formData.phone} onChange={e => setFormData(prev => ({
          ...prev,
          phone: e.target.value
        }))} className="bg-card text-foreground border-border focus-visible:ring-ring" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="form-email" className="text-foreground text-sm font-medium">
            Email Address <span className="text-destructive">*</span>
          </label>
          <Input id="form-email" name="email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => setFormData(prev => ({
          ...prev,
          email: e.target.value
        }))} className="bg-card text-foreground border-border focus-visible:ring-ring" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="form-address" className="text-foreground text-sm font-medium">
            Street Address or ZIP Code <span className="text-destructive">*</span>
          </label>
          <Input id="form-address" name="address" type="text" required placeholder="e.g. 123 Main St or 30030" value={formData.address} onChange={e => setFormData(prev => ({
          ...prev,
          address: e.target.value
        }))} className="bg-card text-foreground border-border focus-visible:ring-ring" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="form-project-type" className="text-foreground text-sm font-medium">
            Project Type <span className="text-destructive">*</span>
          </label>
          <Select value={formData.project_type} onValueChange={value => setFormData(prev => ({
          ...prev,
          project_type: value
        }))} required>
            <SelectTrigger id="form-project-type" className="w-full bg-card text-foreground border-border focus-visible:ring-ring">
              <SelectValue placeholder="Select project type" />
            </SelectTrigger>
            <SelectContent>
              {projectTypeOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                  {opt}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="form-contact-method" className="text-foreground text-sm font-medium">
            Preferred Contact Method <span className="text-destructive">*</span>
          </label>
          <Select value={formData.contact_method} onValueChange={value => setFormData(prev => ({
          ...prev,
          contact_method: value
        }))} required>
            <SelectTrigger id="form-contact-method" className="w-full bg-card text-foreground border-border focus-visible:ring-ring">
              <SelectValue placeholder="Select contact method" />
            </SelectTrigger>
            <SelectContent>
              {contactMethodOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                  {opt}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="form-timing" className="text-foreground text-sm font-medium">
            Estimated Timing <span className="text-destructive">*</span>
          </label>
          <Select value={formData.timing} onValueChange={value => setFormData(prev => ({
          ...prev,
          timing: value
        }))} required>
            <SelectTrigger id="form-timing" className="w-full bg-card text-foreground border-border focus-visible:ring-ring">
              <SelectValue placeholder="Select timeframe" />
            </SelectTrigger>
            <SelectContent>
              {timingOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                  {opt}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="form-budget" className="text-foreground text-sm font-medium">
            Estimated Budget <span className="text-xs text-muted-foreground">(Optional)</span>
          </label>
          <Select value={formData.budget} onValueChange={value => setFormData(prev => ({
          ...prev,
          budget: value
        }))}>
            <SelectTrigger id="form-budget" className="w-full bg-card text-foreground border-border focus-visible:ring-ring">
              <SelectValue placeholder="Select budget range" />
            </SelectTrigger>
            <SelectContent>
              {budgetOptions.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                  {opt}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="form-details" className="text-foreground text-sm font-medium">
          Project Details <span className="text-xs text-muted-foreground">(Optional)</span>
        </label>
        <Textarea id="form-details" name="details" placeholder="Tell us about your home and remodeling goals..." value={formData.details} onChange={e => setFormData(prev => ({
        ...prev,
        details: e.target.value
      }))} className="bg-card text-foreground border-border focus-visible:ring-ring min-h-[110px]" />
      </div>

      <div className="pt-2">
        <label className="flex items-start gap-3 cursor-pointer text-xs sm:text-sm text-foreground">
          <input type="checkbox" required checked={formData.consent} onChange={e => setFormData(prev => ({
          ...prev,
          consent: e.target.checked
        }))} className="mt-1 size-4 rounded border-border text-primary focus:ring-ring accent-primary" />
          <span className="leading-snug">
            I agree to be contacted about my project by phone, text, or email. <span className="text-destructive">*</span>
          </span>
        </label>
      </div>

      {submitError && <div className="p-3.5 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm flex items-start gap-2">
          <AlertCircle className="size-4 shrink-0 mt-0.5" />
          <span>{submitError}</span>
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting || !formData.project_type || !formData.contact_method || !formData.timing || !formData.consent} className="w-full py-6 text-base font-semibold transition-[color,background-color,border-color,box-shadow]">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-5" />
              Processing request...
            </> : <>
              Request an estimate
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="size-4 text-primary" />
          <span>Your information is kept strictly confidential and never shared.</span>
        </div>
      </div>
    </form>;
}
export default function Estimate() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const containerVariant = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  return <section id="request-an-estimate" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeUpVariant} className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-semibold text-xs tracking-wide uppercase">
              Project Consultation
            </Badge>
            <div className="h-0.5 w-8 bg-primary rounded-lg" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground mb-4">
            Request an estimate
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Tell us about your home and remodeling plans. We will schedule a site visit, review structural details, and deliver a transparent, itemized proposal.
          </p>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Direct Contact & Info */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariant} className="lg:col-span-5 space-y-6">
            <motion.div variants={fadeUpVariant}>
              <Card className="bg-card/90 backdrop-blur-md border border-border shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-serif text-foreground">
                    Direct studio contact
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm">
                    Reach our Decatur project coordinators directly for questions or scheduling.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {contactCards.map((item, i) => {
                  const IconComponent = item.icon;
                  const content = <div className="flex items-start gap-3.5">
                        <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0">
                          <IconComponent className="size-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {item.title}
                          </p>
                          <p className="text-sm font-medium text-foreground truncate mt-0.5">
                            {item.detail}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {item.note}
                          </p>
                        </div>
                      </div>;
                  return <div key={item.title} data-index={i} className="p-3 rounded-lg bg-secondary/50 border border-border/70 hover:border-primary/40 transition-[color,background-color,border-color,box-shadow]">
                        {item.action ? <Link to={item.action} className="block text-foreground hover:text-primary transition-colors">
                            {content}
                          </Link> : content}
                      </div>;
                })}
                </CardContent>
              </Card>
            </motion.div>

            {/* Consultation Process Snapshot */}
            <motion.div variants={fadeUpVariant}>
              <Card className="bg-card/80 border border-border p-6 shadow-sm space-y-4">
                <div className="flex items-center gap-2 text-foreground font-serif font-semibold text-lg">
                  <CalendarCheck className="size-5 text-primary" />
                  <span>What happens next</span>
                </div>
                <div className="space-y-3">
                  {perks.map((perk, i) => <div key={perk.title} data-index={i} className="flex items-start gap-3">
                      <div className="size-5 rounded-lg bg-accent text-primary flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 className="size-3.5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{perk.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{perk.description}</p>
                      </div>
                    </div>)}
                </div>
              </Card>
            </motion.div>

          </motion.div>

          {/* Right Column: Estimate Request Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUpVariant} className="lg:col-span-7">
            <Card className="bg-card/95 backdrop-blur-md border border-border shadow-md rounded-xl overflow-hidden">
              <div className="h-1 bg-primary w-full" />
              <CardHeader className="pt-6 sm:pt-8 px-6 sm:px-8 pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-2xl sm:text-3xl font-serif text-foreground">
                      Request an Estimate
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm mt-1">
                      Complete this form to start your residential remodeling plan.
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="hidden sm:inline-flex border-border text-foreground">
                    Free Consultation
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="px-6 sm:px-8 pb-8">
                <EstimateForm />
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}